import { stashedViewQuery } from '@/api/queries'
import { ChatSidebar } from '@/components/chat/ChatSidebar'
import { LoadingCard } from '@/components/ui/StateCards'
import {
  ChatConversationProvider,
  useChatConversation,
} from '@/components/chat/useChatConversation'
import { EventDrawer } from '@/components/drawers/EventDrawer'
import { Sala } from '@/components/sala/Sala'
import { useAuth, useCan } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { useTheme } from '@/lib/theme'
import { LangToggle } from '@/lib/i18n/LangToggle'
import { useViewIntentDispatcher } from '@/lib/useViewIntentDispatcher'
import { parseSalaParam } from '@/lib/viewIntent'
import { useQuery } from '@tanstack/react-query'
import { Link, Outlet, useLocation, useNavigate, useRouter } from '@tanstack/react-router'
import { BarChart3, LogOut, Moon, Settings, Sun, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { t } from '@/lib/i18n'

export function AppShell() {
  const { theme, toggle } = useTheme()
  const { status, user, logout } = useAuth()
  const canChat = useCan('chat.use')
  const canManageUsers = useCan('user.manage')
  // WP2A3B: "LLM" usage stats and the running build's version number are
  // internal/administrative -- hidden from the main owner-facing shell
  // for a standard view-only client. chat.tool.get_llm_stats is only ever
  // granted to admins (server/permissions.py MCP_ADMIN_READ_TOOLS), never
  // auto-granted to a plain chat.use viewer, so it doubles as the "is
  // this an authorised internal user" check here.
  const canSeeInternalTools = useCan('chat.tool.get_llm_stats')
  const navigate = useNavigate()
  const location = useLocation()
  const dispatchViewIntent = useViewIntentDispatcher()
  // CDV-024 / TECHNICAL.md §4.13.7: lift the chat-send pipeline so a form
  // rendered inside the sala can submit through the same conversation
  // even when the sidebar is collapsed (CDV-016 default).
  const conversation = useChatConversation({ onViewIntent: dispatchViewIntent })
  const salaActive = Boolean((location.search as { sala?: unknown }).sala)
  // WP-polish: lifted out of ChatSidebar so the main content area can
  // reserve room for the docked desktop panel instead of being covered
  // by it (section 7 of the polish brief).
  const [chatOpen, setChatOpen] = useState(false)
  // 12 Oct 2026 (MVP spec): Zoe holds the chat on the site page -- the
  // floating launcher is hidden there, and her "Ask Zoe" button opens it.
  const isSiteMainPage = /^\/sites\/[^/]+\/?$/.test(location.pathname)
  useEffect(() => {
    const open = () => setChatOpen(true)
    window.addEventListener('zonoo:open-chat', open)
    return () => window.removeEventListener('zonoo:open-chat', open)
  }, [])
  const isLoginRoute = location.pathname === '/login'
  // 24 Oct 2026: the weekly report pages own the whole page (no shell);
  // /r/<token> is public (private link, no login).
  const isPublicReport = location.pathname.startsWith('/r/')
  const isReportPage = isPublicReport || location.pathname.startsWith('/report/')

  // Gate everything behind a session. /login is the one public route.
  useEffect(() => {
    if (status === 'anon' && !isLoginRoute && !isPublicReport) {
      navigate({
        to: '/login',
        search: { next: location.pathname === '/' ? undefined : location.pathname },
        replace: true,
      })
    }
  }, [status, isLoginRoute, isPublicReport, navigate, location.pathname])

  // 19 Oct 2026: someone still on the password an admin gave them must
  // choose their own before seeing anything else (the server enforces it too).
  const mustChange = status === 'authed' && Boolean(user?.must_change_password)
  useEffect(() => {
    if (mustChange && location.pathname !== '/account' && !isPublicReport) {
      navigate({ to: '/account', replace: true })
    }
  }, [mustChange, isPublicReport, navigate, location.pathname])

  if (isPublicReport || (isReportPage && status === 'authed')) {
    return <Outlet />
  }

  if (isLoginRoute) {
    // Login page owns its own full-viewport layout; skip the shell.
    return <Outlet />
  }

  if (status === 'loading' || (status === 'anon' && !isLoginRoute)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-foreground-muted text-sm">
        Loading…
      </div>
    )
  }

  return (
    <ChatConversationProvider value={conversation}>
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* 7 Oct 2026 (Apple polish): sticky, lightly frosted top bar -- stays
          reachable while the page scrolls under it. `frosted` turns solid for
          reduced-transparency / high-contrast users (globals.css). */}
      <nav className="frosted sticky top-0 z-30 bg-card/80 backdrop-blur-xl backdrop-saturate-150 border-b border-border/60 py-3">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="zonoo.ai" className="inline-flex items-center gap-2">
              {/* WP2A6C: just the single zonoo.ai wordmark, shown at every
                  width (no separate Z symbol alongside it) and sized up
                  since it's now the only brand mark in the nav. */}
              <img
                src={theme === 'dark' ? '/brand/zonoo-ai-light.png' : '/brand/zonoo-ai-dark.png'}
                alt=""
                aria-hidden="true"
                className="h-6 w-auto"
              />
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {canSeeInternalTools && (
              <Link
                to="/llm"
                title={t('AI cost')}
                className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-primary px-2 py-1 rounded hover:bg-accent transition-colors"
              >
                <BarChart3 className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">{t('AI cost')}</span>
              </Link>
            )}
            {canManageUsers && (
              <Link
                to="/users"
                title={t('Users')}
                className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-primary px-2 py-1 rounded hover:bg-accent transition-colors"
              >
                <Users className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">{t('Users')}</span>
              </Link>
            )}
            <Link
              to="/account"
              title={t('Account')}
              className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-primary px-2 py-1 rounded hover:bg-accent transition-colors"
            >
              <Settings className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">{t('Account')}</span>
            </Link>
            <button
              type="button"
              onClick={toggle}
              aria-label={theme === 'dark' ? t('Switch to light mode') : t('Switch to dark mode')}
              title={theme === 'dark' ? t('Light mode') : t('Dark mode')}
              className="inline-flex items-center justify-center text-foreground-muted hover:text-primary px-2 py-1 rounded hover:bg-accent transition-colors"
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4" aria-hidden="true" />
              ) : (
                <Moon className="w-4 h-4" aria-hidden="true" />
              )}
            </button>
            <LangToggle />
            {user && (
              <>
                <span
                  className="hidden sm:inline text-xs text-foreground-muted tabular-nums"
                  title={user.email}
                >
                  {user.display_name || user.email}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    await logout()
                    navigate({ to: '/login', search: { next: undefined }, replace: true })
                  }}
                  title={t('Log out')}
                  aria-label={t('Log out')}
                  className="inline-flex items-center justify-center text-foreground-muted hover:text-primary px-2 py-1 rounded hover:bg-accent transition-colors"
                >
                  <LogOut className="w-4 h-4" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        </div>
      </nav>
      <main
        className={cn(
          'flex-1 w-full transition-[padding] duration-200',
          chatOpen ? 'lg:pr-[440px]' : '',
        )}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <ShellBody />
        </div>
      </main>
      <footer
        className={cn(
          'border-t border-border py-3 transition-[padding] duration-200',
          chatOpen ? 'lg:pr-[440px]' : '',
        )}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between text-xs text-subtle-foreground">
          <span>zonoo.ai</span>
          {canSeeInternalTools ? <span className="tabular-nums">v{__APP_VERSION__}</span> : null}
        </div>
      </footer>
      {canChat && <ChatSidebar salaActive={salaActive} open={chatOpen} onOpenChange={setChatOpen} hideLauncher={isSiteMainPage} />}
    </div>
    </ChatConversationProvider>
  )
}

// Mount the chat-driven sala when ?sala= is present in the URL, otherwise
// render the normal route outlet. Kept as an inner component so it can
// re-read the location without re-rendering the whole shell.
//
// Two encodings on the search param:
//   - `?sala=<json>`        — single-kind salas inline the JSON
//   - `?sala=ref:<id>`      — composed salas above the URL budget pull
//                             the payload from /api/chat/views/<id>
function ShellBody() {
  const router = useRouter()
  const salaRaw = useLocation({
    select: (s) => (s.search as { sala?: unknown }).sala,
  })
  const refId = useMemo(() => parseSalaRef(salaRaw), [salaRaw])
  const inlineIntent = useMemo(
    () => (refId ? null : parseSalaParam(salaRaw)),
    [salaRaw, refId],
  )
  const refQ = useQuery({
    ...stashedViewQuery(refId ?? ''),
    enabled: refId !== null,
  })
  const stashIntent = useMemo(
    () => (refQ.data ? parseSalaParam(refQ.data.payload) : null),
    [refQ.data],
  )
  const intent = inlineIntent ?? stashIntent
  const evidenceRaw = useLocation({
    select: (s) => (s.search as { evidence?: unknown }).evidence,
  })
  const evidence = useMemo(() => parseEvidence(evidenceRaw), [evidenceRaw])
  if (refId && refQ.isPending) {
    return (
      <LoadingCard label={t('Loading view…')} />
    )
  }
  if (refId && !intent) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-foreground-muted">
        {t('This composed view is no longer available.')}
      </div>
    )
  }
  if (!intent) {
    return (
      <>
        <Outlet />
        {evidence ? (
          <EvidenceDrawer
            key={evidence.ids.join(',')}
            siteId={evidence.siteId}
            cameraId={evidence.cameraId}
            ids={evidence.ids}
            onClose={() =>
              router.navigate({
                to: router.state.location.pathname,
                resetScroll: false,
                search: (prev: Record<string, unknown>) => {
                  const { evidence: _ev, ...rest } = prev
                  return rest
                },
              } as never)
            }
          />
        ) : null}
      </>
    )
  }
  // Back button: exit the sala view entirely. If the user clicked into a
  // capture (event param) and then pressed back, a plain history.back()
  // would only close the drawer and leave the sala mounted — not what
  // the header arrow promises. Strip both `sala` and `event` from the
  // URL in one go so the shell drops straight back to the underlying
  // route (drawer closed, sala gone).
  const exitSala = () =>
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: Record<string, unknown>) => {
        const { sala: _s, event: _e, ...rest } = prev
        return rest
      },
    } as never)
  return <Sala intent={intent} onBack={exitSala} />
}

/** 15 Oct 2026: `?evidence=<photos intent JSON>` from the chat's "See the
 *  evidence" button. Returns the site, camera and photo ids, oldest first. */
export function parseEvidence(
  raw: unknown,
): { siteId: string; cameraId: string | null; ids: number[] } | null {
  const intent = parseSalaParam(raw)
  if (!intent || intent.kind !== 'photos' || !intent.site_id) return null
  const ids = [...(intent.event_ids ?? [])].filter((n) => Number.isFinite(n)).sort((a, b) => a - b)
  if (ids.length === 0) return null
  return { siteId: intent.site_id, cameraId: intent.camera_id ?? null, ids }
}

function EvidenceDrawer({
  siteId,
  cameraId,
  ids,
  onClose,
}: {
  siteId: string
  cameraId: string | null
  ids: number[]
  onClose: () => void
}) {
  const [eventId, setEventId] = useState<number>(ids[0] as number)
  return (
    <EventDrawer
      siteId={siteId}
      eventId={eventId}
      cameraId={cameraId}
      navList={ids}
      onClose={onClose}
      onNavigate={setEventId}
    />
  )
}

/** CDV-029: detect the `?sala=ref:<id>` form. Returns the id when the
 *  param matches, otherwise null. */
function parseSalaRef(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  if (!raw.startsWith('ref:')) return null
  const id = raw.slice(4)
  return id.length > 0 ? id : null
}
